<script setup lang="ts">
import { computed } from 'vue'
import {
  BookTemplate,
  ClipboardPaste,
  Copy,
  CopyPlus,
  CreditCard,
  Eraser,
  Keyboard,
  LayoutGrid,
  Move,
  Save,
  Scissors,
  Settings2,
  Shapes,
  Trash2,
} from 'lucide-vue-next'
import { useFlowEditorContext } from '../composables/useFlowEditor'

/** Top bar: inspector panels, canvas tool, selection actions, save state and buttons. */
const editor = useFlowEditorContext()
const { inspectorPanel, interactionTool } = editor.ui
const { hasSelection, selectedNodeIds, clearSelection } = editor.selection
const { clipboard, duplicateSelected, copySelected, cutSelected, pasteSelected, removeSelected } =
  editor.editing
const { saveSelectionAsTemplate } = editor.templates
const { saveStatusLabel } = editor.history
const { remoteStatus } = editor.persistence
const { saveFlow, cancelFlow } = editor
const hasClipboard = computed(() => Boolean(clipboard.value))
</script>

<template>
  <section class="editor-toolbox" aria-label="Flow manager toolbar">
    <div class="tool-row" role="toolbar" aria-label="Inspector panel tabs">
      <button
        type="button"
        class="icon-btn"
        :class="{ active: inspectorPanel === 'flow' }"
        :title="$t('flow.nodeSettings')"
        @click="inspectorPanel = 'flow'"
      >
        <CreditCard aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn"
        :class="{ active: inspectorPanel === 'templates' }"
        :title="$t('flow.templates')"
        @click="inspectorPanel = 'templates'"
      >
        <BookTemplate aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn"
        :class="{ active: inspectorPanel === 'canvas' }"
        :title="$t('flow.canvasSettings')"
        @click="inspectorPanel = 'canvas'"
      >
        <Settings2 aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn"
        :class="{ active: inspectorPanel === 'shortcuts' }"
        :title="$t('flow.shortcuts')"
        @click="inspectorPanel = 'shortcuts'"
      >
        <Keyboard aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn"
        :class="{ active: inspectorPanel === 'visual' }"
        :title="$t('flow.visualSettings')"
        @click="inspectorPanel = 'visual'"
      >
        <Shapes aria-hidden="true" />
      </button>
    </div>

    <div class="fancy-divider" aria-hidden="true" />

    <div class="tool-row" role="toolbar" aria-label="Canvas interaction tools">
      <button
        type="button"
        class="icon-btn"
        :class="{ active: interactionTool === 'pan' }"
        :title="$t('flow.toolbarPanTool')"
        @click="interactionTool = 'pan'"
      >
        <Move aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn"
        :class="{ active: interactionTool === 'marquee' }"
        :title="$t('flow.toolbarMarqueeTool')"
        @click="interactionTool = 'marquee'"
      >
        <LayoutGrid aria-hidden="true" />
      </button>
    </div>

    <div class="fancy-divider" aria-hidden="true" />

    <div class="tool-row" role="toolbar" aria-label="Selection actions">
      <button
        type="button"
        class="icon-btn"
        :disabled="!hasSelection"
        :title="$t('flow.toolbarDuplicate')"
        @click="duplicateSelected"
      >
        <CopyPlus aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn"
        :disabled="!hasSelection"
        :title="$t('flow.toolbarCopy')"
        @click="copySelected"
      >
        <Copy aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn"
        :disabled="!hasSelection"
        :title="$t('flow.toolbarCut')"
        @click="cutSelected"
      >
        <Scissors aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn"
        :disabled="!hasClipboard"
        :title="$t('flow.toolbarPaste')"
        @click="pasteSelected"
      >
        <ClipboardPaste aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn danger"
        :disabled="!hasSelection"
        :title="$t('flow.toolbarDelete')"
        @click="removeSelected"
      >
        <Trash2 aria-hidden="true" />
      </button>
      <button
        type="button"
        class="icon-btn"
        :disabled="!hasSelection"
        :title="$t('flow.toolbarClearSelection')"
        @click="clearSelection"
      >
        <Eraser aria-hidden="true" />
      </button>
      <button
        v-if="selectedNodeIds.length > 0"
        type="button"
        class="icon-btn"
        :title="$t('flow.toolbarSaveSelectionAsTemplate')"
        @click="saveSelectionAsTemplate"
      >
        <BookTemplate aria-hidden="true" />
      </button>
    </div>

    <div class="toolbox-meta" aria-label="Save actions">
      <div v-if="saveStatusLabel" class="save-status" aria-live="polite">
        {{ saveStatusLabel }}
      </div>
      <div v-if="remoteStatus" class="remote-status" aria-live="polite">
        {{ remoteStatus }}
      </div>
      <button type="button" class="pl-btn pl-btn--secondary" @click="cancelFlow">
        {{ $t('flow.cancel') }}
      </button>
      <button type="button" class="pl-btn pl-btn--primary" @click="saveFlow">
        <Save aria-hidden="true" />
        {{ $t('flow.save') }}
      </button>
    </div>
  </section>
</template>

<style scoped>
.editor-toolbox {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  padding: 0.15rem 0;
}

.tool-row {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  align-items: center;
}

.toolbox-meta {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex-wrap: nowrap;
  justify-content: flex-end;
}

.save-status {
  font-size: 0.85rem;
  color: #475569;
  white-space: nowrap;
}

.remote-status {
  font-size: 0.85rem;
  color: #475569;
  white-space: nowrap;
}

.fancy-divider {
  width: 22px;
  height: 2.25rem;
  margin: 0 0.65rem;
  position: relative;
  flex: 0 0 auto;
}

.fancy-divider::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 100%;
  background: #e2e8f0;
}

.fancy-divider::after {
  content: '';
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: #94a3b8;
  box-shadow:
    0 -8px 0 #94a3b8,
    0 8px 0 #94a3b8;
  opacity: 0.9;
}
</style>

<style scoped src="./styles/iconButton.css"></style>
